import React, { useEffect, useState } from 'react';
import { OrderType, Booklet, Order } from '../../types/generated.types';
import { useQuery } from '@apollo/client';
import { completedOrdersQuery } from '../../queries/queries';
import Modal from '../../common/Modal';
import FieldWrapper from '../../common/FieldWrapper';
import BookletOrderBatchItem from './BookletOrderBatchItem';
import BookletDownloadPDFModal from './BookletDownloadPDFModal';


interface OrderBatch {
  batchLabel: string;
  completedDate: string;
  orders: Order[];
}

interface Props {
  booklet: Booklet;
}

const BookletsPreviousOrderBatches: React.FC<Props> = ({ booklet }) => {
  const { data, loading } = useQuery(completedOrdersQuery, { variables: { bookletId: booklet && booklet._id } });

  const [voucherOrderBatches, setVoucherOrderBatches] = useState<OrderBatch[]>([]);
  const [webshopOrdersBatch, setWebshopOrdersBatch] = useState<OrderBatch | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<OrderBatch | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set order batches
  useEffect(() => {
    if (data?.completedOrdersByBooklet) {
      // Split orders by type
      const voucherOrders: Order[] = data.completedOrdersByBooklet.filter((order: Order) => order.type === OrderType.Voucher);
      const purchaseOrders: Order[] = data.completedOrdersByBooklet.filter((order: Order) => order.type === OrderType.Purchase);

      // Handle webshop orders (purchase)
      if (purchaseOrders.length > 0) {
        const completedDate = new Date(purchaseOrders[0].completedDate).toLocaleDateString();
        const webshopBatch: OrderBatch = {
          batchLabel: 'Ordrar via webshop',
          completedDate,
          orders: purchaseOrders
        };
        setWebshopOrdersBatch(webshopBatch);
      } else {
        setWebshopOrdersBatch(null);
      }

      // Handle vouchers and group by batchLabel
      const batchMap = new Map();
      voucherOrders.forEach(order => {
        if (!batchMap.has(order.batchLabel)) {
          batchMap.set(order.batchLabel, []);
        }
        batchMap.get(order.batchLabel).push(order);
      });

      // Sort batches by completedDate
      const sortedBatches = Array.from(batchMap.entries()).sort((a, b) => {
        const dateA = new Date(a[1][0].completedDate).getTime();
        const dateB = new Date(b[1][0].completedDate).getTime();
        return dateB - dateA;
      });

      // Create array of order batches
      const orderBatchesArray = sortedBatches.map(([batchLabel, orders]) => {
        const completedDate = new Date(orders[0].completedDate).toLocaleDateString();
        return {
          batchLabel,
          completedDate,
          orders
        };
      });

      setVoucherOrderBatches(orderBatchesArray);
    }
  }, [data]);

  // Function to handle opening the modal
  const openModal = (batch: OrderBatch) => {
    setSelectedBatch(batch);
    setIsModalOpen(true);
  };

  // Function to handle closing the modal
  const closeModal = () => {
    setSelectedBatch(null);
    setIsModalOpen(false);
  };

  return (
    <>
      <FieldWrapper name='' title='' x2>
        <div className="flex flex-col text-left gap-1" style={{ maxWidth: '100%' }}>

          {/* Display loading */}
          {loading && <p>Laddar...</p>}

          {/* Display webshop orders */}
          {webshopOrdersBatch && <BookletOrderBatchItem
            key={webshopOrdersBatch.batchLabel}
            batch={webshopOrdersBatch}
            batchLabel={webshopOrdersBatch.batchLabel}
            openModal={openModal}
          />}

          {/* Display voucher batches */}
          {voucherOrderBatches.length > 0 && (
            <div className='flex flex-col'>
              <div className="flex items-center gap-1">
                <small>Genererade värdebevis</small>
              </div>
              <div className="previous-order-batches">
                {voucherOrderBatches?.map(batch => (
                  <BookletOrderBatchItem
                    key={batch.batchLabel}
                    batch={batch}
                    batchLabel={batch.batchLabel}
                    openModal={openModal}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Display message if no orders */}
          {!loading && !webshopOrdersBatch && voucherOrderBatches.length === 0 && <p>Inga tidigare ordrar.</p>}

        </div>
      </FieldWrapper>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} closeModal={closeModal} className='order-list-modal'>
          <BookletDownloadPDFModal bookletId={booklet._id} selectedBatch={selectedBatch} />
        </Modal>
      )}
    </>
  );
};

export default BookletsPreviousOrderBatches;
